/**
 * AccentIcon — a part of a record screen's icon (Open jobs, Vehicles…) on a
 * soft tint of its accent color. The icon and color come from the shared
 * accent map (packages/ui/src/theme/accents.ts) so web and app match; this
 * maps the Lucide names to `lucide-react`. Decorative: a word always goes with
 * it.
 */
import React from 'react';
import {
  AtSign,
  CalendarCheck,
  Car,
  CircleAlert,
  CircleCheck,
  ClipboardCheck,
  Clock,
  LifeBuoy,
  Receipt,
  Store,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { accentSizes, resolveAccent, tokenVars, type AccentIconName, type AccentKey, type AccentSize } from '@turn-connect/ui';

const ICONS: Record<AccentIconName, LucideIcon> = {
  wrench: Wrench,
  'circle-check': CircleCheck,
  car: Car,
  receipt: Receipt,
  'circle-alert': CircleAlert,
  clock: Clock,
  'clipboard-check': ClipboardCheck,
  'calendar-check': CalendarCheck,
  'at-sign': AtSign,
  'life-buoy': LifeBuoy,
  store: Store,
};

export interface AccentIconProps {
  accent: AccentKey;
  /** `tile` 32px (stat tiles, lists), `band` 40px (section header band). */
  size?: AccentSize;
  /** False greys it out (nothing waiting). */
  active?: boolean;
  testId?: string;
}

export const AccentIcon: React.FC<AccentIconProps> = ({ accent, size = 'tile', active = true, testId }) => {
  const a = resolveAccent(tokenVars, accent, active);
  const s = accentSizes[size];
  const Icon = ICONS[a.icon];
  return (
    <span
      aria-hidden
      data-testid={testId}
      data-accent={accent}
      data-icon={a.icon}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: s.box,
        height: s.box,
        borderRadius: s.radius,
        background: a.tint,
        color: a.color,
        flexShrink: 0,
      }}
    >
      <Icon size={s.icon} strokeWidth={2} />
    </span>
  );
};

export default AccentIcon;
